import { htmlToEscPosRaster } from "./HtmlToEscPosRaster";

/* ===========================================================
   BLUETOOTH / ESC-POS SERVİSİ

   MOBİL:
   Web Bluetooth -> BLE GATT -> ESC/POS TEXT

   Bu sürümde mobil fişin TAMAMI Font B kullanır ve
   64 karakter genişlik esas alınır.

   Amaç:
   - Altın Alındı / Altın Verildi çıktısındaki düzgün görünümü
     diğer işlem türlerine de uygulamak.
   - Nakit, Ziynet, Altın Karşılığı Nakit, Bağlantılı İşlem,
     Bakiye ve Ziynet Bakiyelerini aynı font / hizaya getirmek.

   PC:
   printHtmlFallback() ile mevcut HTML baskısı devam eder.
=========================================================== */

let bluetoothDevice = null;
let gattServer = null;
let writeCharacteristic = null;

const uuid16 = (shortUuid) =>
  `0000${String(shortUuid).toLowerCase()}-0000-1000-8000-00805f9b34fb`;

/* Yaygın BLE UART / termal yazıcı profilleri. */
const BLE_PROFILES = [
  {
    service: uuid16("ffe0"),
    characteristics: [uuid16("ffe1")],
  },
  {
    service: uuid16("fff0"),
    characteristics: [uuid16("fff1"), uuid16("fff2")],
  },
  {
    service: uuid16("ff00"),
    characteristics: [uuid16("ff01"), uuid16("ff02")],
  },
  {
    service: uuid16("ae30"),
    characteristics: [uuid16("ae01"), uuid16("ae02")],
  },
  {
    service: uuid16("18f0"),
    characteristics: [uuid16("2af1")],
  },
  {
    service: "49535343-fe7d-4ae5-8fa9-9fafd205e455",
    characteristics: ["49535343-8841-43f4-a8d4-ecbe34729bb3"],
  },
  {
    service: "6e400001-b5a3-f393-e0a9-e50e24dcca9e",
    characteristics: ["6e400002-b5a3-f393-e0a9-e50e24dcca9e"],
  },
];

const BLE_SERVICE_UUIDS = [
  ...new Set(BLE_PROFILES.map((x) => x.service)),
];

/* ===========================================================
   ORTAM KONTROLLERİ
=========================================================== */

export const isWebBluetoothSupported = () =>
  typeof navigator !== "undefined" &&
  typeof navigator.bluetooth !== "undefined" &&
  typeof navigator.bluetooth.requestDevice === "function";

export const isIOSDevice = () => {
  if (typeof navigator === "undefined") {
    return false;
  }

  const ua = navigator.userAgent || "";
  const platform = navigator.platform || "";

  return (
    /iPhone|iPad|iPod/i.test(ua) ||
    (
      platform === "MacIntel" &&
      Number(navigator.maxTouchPoints || 0) > 1
    )
  );
};

export const isMobileDevice = () => {
  if (typeof navigator === "undefined") {
    return false;
  }

  const ua = navigator.userAgent || "";

  return (
    isIOSDevice() ||
    /Android|Mobile/i.test(ua)
  );
};

export const shouldUseBluetoothPrint = () =>
  isMobileDevice() &&
  isWebBluetoothSupported();

/* ===========================================================
   BAĞLANTI DURUMU
=========================================================== */

export const isBluetoothPrinterConnected = () =>
  Boolean(
    bluetoothDevice?.gatt?.connected &&
      writeCharacteristic
  );

const resetConnection = () => {
  bluetoothDevice = null;
  gattServer = null;
  writeCharacteristic = null;
};

const handleGattDisconnected = () => {
  console.warn(
    "Bluetooth yazıcı bağlantısı kesildi."
  );

  resetConnection();
};

const characteristicWritable = (
  characteristic
) =>
  Boolean(
    characteristic?.properties?.write ||
      characteristic?.properties?.writeWithoutResponse
  );

/* ===========================================================
   YAZILABİLİR CHARACTERISTIC BUL
=========================================================== */

const findWritableCharacteristic = async (
  server
) => {
  for (const profile of BLE_PROFILES) {
    let service;

    try {
      service =
        await server.getPrimaryService(
          profile.service
        );
    } catch {
      continue;
    }

    for (
      const characteristicUuid of
      profile.characteristics
    ) {
      try {
        const characteristic =
          await service.getCharacteristic(
            characteristicUuid
          );

        if (
          characteristicWritable(
            characteristic
          )
        ) {
          console.log(
            "Bluetooth yazma kanalı bulundu:",
            {
              serviceUuid:
                profile.service,

              characteristicUuid:
                characteristic.uuid,

              write:
                characteristic.properties?.write,

              writeWithoutResponse:
                characteristic.properties
                  ?.writeWithoutResponse,
            }
          );

          return characteristic;
        }
      } catch {
        // Bu characteristic bu serviste bulunmayabilir.
      }
    }

    try {
      const characteristics =
        await service.getCharacteristics();

      const writable =
        characteristics.find(
          characteristicWritable
        );

      if (writable) {
        console.log(
          "Bluetooth yazma kanalı otomatik bulundu:",
          {
            serviceUuid:
              profile.service,

            characteristicUuid:
              writable.uuid,
          }
        );

        return writable;
      }
    } catch {
      // Servisin characteristic listesi okunamayabilir.
    }
  }

  /*
   * Bazı Web Bluetooth uygulamaları izin verilen servisleri
   * getPrimaryServices() üzerinden döndürebilir.
   */
  if (
    typeof server?.getPrimaryServices ===
    "function"
  ) {
    try {
      const services =
        await server.getPrimaryServices();

      for (const service of services) {
        try {
          const characteristics =
            await service.getCharacteristics();

          const writable =
            characteristics.find(
              characteristicWritable
            );

          if (writable) {
            console.log(
              "Bluetooth yazma kanalı genel taramada bulundu:",
              {
                serviceUuid:
                  service.uuid,

                characteristicUuid:
                  writable.uuid,
              }
            );

            return writable;
          }
        } catch {
          // Diğer servise geç.
        }
      }
    } catch {
      // Tarayıcı tüm servisleri listelemeye izin vermeyebilir.
    }
  }

  return null;
};

/* ===========================================================
   BLUETOOTH BAĞLANTISI
=========================================================== */

export const connectBluetoothPrinter = async () => {
  if (
    !isWebBluetoothSupported()
  ) {
    throw new Error(
      "Bu tarayıcı Web Bluetooth desteklemiyor. Web Bluetooth destekleyen bir tarayıcı kullanın."
    );
  }

  if (
    isBluetoothPrinterConnected()
  ) {
    return bluetoothDevice;
  }

  /*
   * Aynı oturum içinde daha önce seçilmiş cihazı
   * tekrar bağlamayı dene.
   */
  if (
    bluetoothDevice?.gatt &&
    !bluetoothDevice.gatt.connected
  ) {
    try {
      gattServer =
        await bluetoothDevice.gatt.connect();

      writeCharacteristic =
        await findWritableCharacteristic(
          gattServer
        );

      if (writeCharacteristic) {
        return bluetoothDevice;
      }
    } catch (
      reconnectError
    ) {
      console.warn(
        "Bluetooth yeniden bağlantı başarısız:",
        reconnectError
      );

      resetConnection();
    }
  }

  /* =========================================================
     CİHAZ SEÇİMİ
  ========================================================= */

  const device =
    await navigator.bluetooth.requestDevice(
      {
        acceptAllDevices: true,
        optionalServices:
          BLE_SERVICE_UUIDS,
      }
    );

  bluetoothDevice =
    device;

  bluetoothDevice.addEventListener?.(
    "gattserverdisconnected",
    handleGattDisconnected
  );

  if (
    !bluetoothDevice.gatt
  ) {
    resetConnection();

    throw new Error(
      "Seçilen Bluetooth cihazında GATT desteği bulunamadı."
    );
  }

  /* =========================================================
     GATT CONNECT
  ========================================================= */

  gattServer =
    await bluetoothDevice.gatt.connect();

  /* =========================================================
     WRITE CHARACTERISTIC
  ========================================================= */

  writeCharacteristic =
    await findWritableCharacteristic(
      gattServer
    );

  if (!writeCharacteristic) {
    try {
      bluetoothDevice
        ?.gatt
        ?.disconnect();
    } catch {
      // ignore
    }

    resetConnection();

    throw new Error(
      "Yazıcı üzerinde Web Bluetooth ile yazılabilir BLE kanalı bulunamadı. " +
        "Yazıcı Bluetooth Classic/SPP kullanıyor olabilir veya farklı bir BLE servis UUID'sine sahip olabilir."
    );
  }

  console.log(
    "Bluetooth yazıcı bağlantısı hazır:",
    bluetoothDevice.name ||
      bluetoothDevice.id
  );

  return bluetoothDevice;
};

/* ===========================================================
   BAĞLANTIYI KES
=========================================================== */

export const disconnectBluetoothPrinter = () => {
  try {
    if (
      bluetoothDevice
        ?.gatt
        ?.connected
    ) {
      bluetoothDevice.gatt.disconnect();
    }
  } finally {
    resetConnection();
  }
};

/* ===========================================================
   ESC/POS TEXT ALTYAPISI
=========================================================== */

const ESC = 0x1b;
const GS = 0x1d;

/*
 * FONT B
 *
 * Mevcut Altın Alındı / Altın Verildi çıktısındaki
 * düzgün görünüm 64 karakter Font B yapısından geliyor.
 *
 * Bu nedenle artık bütün mobil fiş:
 *
 * FONT B
 * 64 KARAKTER
 *
 * kullanacak.
 */
const LINE_WIDTH = 64;

const encoder =
  new TextEncoder();

/* ===========================================================
   TÜRKÇE / UTF-8 NORMALİZASYONU
=========================================================== */

const normalizePrinterText = (
  value = ""
) => {
  const trNormalized =
    String(value)
      .replace(
        /Ğ/g,
        "G"
      )
      .replace(
        /ğ/g,
        "g"
      )
      .replace(
        /Ü/g,
        "U"
      )
      .replace(
        /ü/g,
        "u"
      )
      .replace(
        /Ş/g,
        "S"
      )
      .replace(
        /ş/g,
        "s"
      )
      .replace(
        /İ/g,
        "I"
      )
      .replace(
        /ı/g,
        "i"
      )
      .replace(
        /Ö/g,
        "O"
      )
      .replace(
        /ö/g,
        "o"
      )
      .replace(
        /Ç/g,
        "C"
      )
      .replace(
        /ç/g,
        "c"
      );

  const ascii =
    typeof trNormalized.normalize ===
    "function"
      ? trNormalized
          .normalize("NFD")
          .replace(
            /[\u0300-\u036f]/g,
            ""
          )
      : trNormalized;

  return ascii.replace(
    /[^\x20-\x7E\n\r\t]/g,
    "?"
  );
};

/* ===========================================================
   TEXT -> BYTE
=========================================================== */

const textBytes = (
  value
) =>
  encoder.encode(
    normalizePrinterText(
      value
    )
  );

/* ===========================================================
   BYTE ARRAY
=========================================================== */

const bytes = (
  ...values
) =>
  new Uint8Array(
    values
  );

/* ===========================================================
   BYTE ARRAY BİRLEŞTİR
=========================================================== */

const concatBytes = (
  ...arrays
) => {
  const valid =
    arrays.filter(
      (array) =>
        array instanceof
        Uint8Array
    );

  const totalLength =
    valid.reduce(
      (
        sum,
        array
      ) =>
        sum +
        array.length,
      0
    );

  const result =
    new Uint8Array(
      totalLength
    );

  let offset = 0;

  for (
    const array of
    valid
  ) {
    result.set(
      array,
      offset
    );

    offset +=
      array.length;
  }

  return result;
};

/* ===========================================================
   SAYI FORMAT
=========================================================== */

const fmtTR = (
  value,
  fractionDigits = 2
) => {
  const number =
    Number(
      value ?? 0
    );

  if (
    !Number.isFinite(
      number
    )
  ) {
    return fractionDigits === 0
      ? "0"
      : "0,00";
  }

  return number.toLocaleString(
    "tr-TR",
    {
      minimumFractionDigits:
        fractionDigits,

      maximumFractionDigits:
        fractionDigits,
    }
  );
};

/* ===========================================================
   TEXT KISALT
=========================================================== */

const truncate = (
  value,
  length
) => {
  const str =
    normalizePrinterText(
      value ?? ""
    );

  return str.length <= length
    ? str
    : str.slice(
        0,
        length
      );
};

/* ===========================================================
   PAD
=========================================================== */

const padRight = (
  value,
  length
) =>
  truncate(
    value,
    length
  ).padEnd(
    length,
    " "
  );

const padLeft = (
  value,
  length
) =>
  truncate(
    value,
    length
  ).padStart(
    length,
    " "
  );

/* ===========================================================
   64 KARAKTER YATAY ÇİZGİ
=========================================================== */

const horizontalLine = () =>
  "-".repeat(
    LINE_WIDTH
  ) + "\n";

/* ===========================================================
   FONT B KOMUTU

   ESC M 1
=========================================================== */

const fontBBytes = () =>
  bytes(
    ESC,
    0x4d,
    0x01
  );

/* ===========================================================
   BÖLÜM BAŞLIĞI

   Bütün fiş Font B.
=========================================================== */

const sectionTitleBytes = (
  title
) =>
  concatBytes(
    /*
     * Font B'yi garanti et.
     */
    fontBBytes(),

    /*
     * Center
     */
    bytes(
      ESC,
      0x61,
      0x01
    ),

    /*
     * Bold ON
     */
    bytes(
      ESC,
      0x45,
      0x01
    ),

    textBytes(
      `${normalizePrinterText(
        title
      )}\n`
    ),

    /*
     * Bold OFF
     */
    bytes(
      ESC,
      0x45,
      0x00
    ),

    /*
     * Left
     */
    bytes(
      ESC,
      0x61,
      0x00
    )
  );

/* ===========================================================
   ALTIN İŞLEMLERİ

   REFERANS TASARIM

   A/V     = 4
   Cinsi   = 14
   Miktar  = 10
   mm      = 5
   Has     = 10
   Ad      = 5
   Isc     = 6
   Toplam  = 10

   TOPLAM = 64 KARAKTER
=========================================================== */

const buildAltinTableBytes = (
  altinList
) => {
  if (
    !Array.isArray(
      altinList
    ) ||
    altinList.length === 0
  ) {
    return new Uint8Array();
  }

  const chunks = [
    sectionTitleBytes(
      "ALTIN ISLEMLERI"
    ),

    fontBBytes(),

    textBytes(
      `${padRight(
        "A/V",
        4
      )}` +
        `${padRight(
          "Cinsi",
          14
        )}` +
        `${padLeft(
          "Miktar",
          10
        )}` +
        `${padLeft(
          "mm",
          5
        )}` +
        `${padLeft(
          "Has",
          10
        )}` +
        `${padLeft(
          "Ad",
          5
        )}` +
        `${padLeft(
          "Isc",
          6
        )}` +
        `${padLeft(
          "Toplam",
          10
        )}\n`
    ),

    textBytes(
      horizontalLine()
    ),
  ];

  for (
    const row of
    altinList
  ) {
    const av =
      row
        ?.islemTur
        ?.endsWith(
          "ALINDI"
        )
        ? "A"
        : "V";

    const toplam =
      (
        Number(
          row?.hasGram
        ) || 0
      ) +
      (
        Number(
          row?.iscilikGram
        ) || 0
      );

    chunks.push(
      textBytes(
        `${padRight(
          av,
          4
        )}` +
          `${padRight(
            row?.stokAdi || "",
            14
          )}` +
          `${padLeft(
            fmtTR(
              row?.miktar,
              2
            ),
            10
          )}` +
          `${padLeft(
            fmtTR(
              row?.milyem,
              0
            ),
            5
          )}` +
          `${padLeft(
            fmtTR(
              row?.hasGram,
              2
            ),
            10
          )}` +
          `${padLeft(
            fmtTR(
              row?.adet,
              0
            ),
            5
          )}` +
          `${padLeft(
            fmtTR(
              row?.iscilik,
              0
            ),
            6
          )}` +
          `${padLeft(
            fmtTR(
              toplam,
              2
            ),
            10
          )}\n`
      )
    );
  }

  chunks.push(
    textBytes(
      horizontalLine()
    )
  );

  return concatBytes(
    ...chunks
  );
};

/* ===========================================================
   ZİYNET İŞLEMLERİ

   Miktar = 16
   Cinsi  = 32
   A/V    = 16

   TOPLAM = 64
=========================================================== */

const buildZiynetTableBytes = (
  ziynetList
) => {
  if (
    !Array.isArray(
      ziynetList
    ) ||
    ziynetList.length === 0
  ) {
    return new Uint8Array();
  }

  const chunks = [
    sectionTitleBytes(
      "ZIYNET ISLEMLERI"
    ),

    fontBBytes(),

    textBytes(
      `${padLeft(
        "Miktar",
        16
      )}` +
        `${padRight(
          "Cinsi",
          32
        )}` +
        `${padLeft(
          "A/V",
          16
        )}\n`
    ),

    textBytes(
      horizontalLine()
    ),
  ];

  for (
    const row of
    ziynetList
  ) {
    const av =
      row
        ?.islemTur
        ?.endsWith(
          "ALINDI"
        )
        ? "Alindi"
        : "Verildi";

    chunks.push(
      textBytes(
        `${padLeft(
          fmtTR(
            row?.miktar,
            0
          ),
          16
        )}` +
          `${padRight(
            row?.stokAdi || "",
            32
          )}` +
          `${padLeft(
            av,
            16
          )}\n`
      )
    );
  }

  chunks.push(
    textBytes(
      horizontalLine()
    )
  );

  return concatBytes(
    ...chunks
  );
};

/* ===========================================================
   NAKİT İŞLEMLERİ

   Miktar = 24
   Birim  = 12
   A/V    = 28

   TOPLAM = 64
=========================================================== */

const buildNakitTableBytes = (
  nakitList
) => {
  if (
    !Array.isArray(
      nakitList
    ) ||
    nakitList.length === 0
  ) {
    return new Uint8Array();
  }

  const chunks = [
    sectionTitleBytes(
      "NAKIT ISLEMLERI"
    ),

    fontBBytes(),

    textBytes(
      `${padLeft(
        "Miktar",
        24
      )}` +
        `${padLeft(
          "Birim",
          12
        )}` +
        `${padLeft(
          "A/V",
          28
        )}\n`
    ),

    textBytes(
      horizontalLine()
    ),
  ];

  for (
    const row of
    nakitList
  ) {
    const av =
      row
        ?.islemTur
        ?.endsWith(
          "ALINDI"
        )
        ? "Alindi"
        : "Verildi";

    chunks.push(
      textBytes(
        `${padLeft(
          fmtTR(
            row?.miktar,
            2
          ),
          24
        )}` +
          `${padLeft(
            row?.birim || "",
            12
          )}` +
          `${padLeft(
            av,
            28
          )}\n`
      )
    );
  }

  chunks.push(
    textBytes(
      horizontalLine()
    )
  );

  return concatBytes(
    ...chunks
  );
};

/* ===========================================================
   ALTIN KARŞILIĞI NAKİT

   A/V      = 4
   Miktar   = 13
   Birim    = 7
   Br.Kur   = 13
   Has Fiy. = 13
   Has Gr.     = 14

   TOPLAM = 64

   Artık eski gibi:

   A Miktar...
   Br.Kur...
   Gram...

   şeklinde 3 satır değil.

   Tek tablo satırı olarak basılır.
=========================================================== */

const buildGramKarNakitBytes = (
  list
) => {
  if (
    !Array.isArray(
      list
    ) ||
    list.length === 0
  ) {
    return new Uint8Array();
  }

  const chunks = [
    sectionTitleBytes(
      "ALTIN KARSILIGI NAKIT"
    ),

    fontBBytes(),

    textBytes(
      `${padRight(
        "A/V",
        4
      )}` +
        `${padLeft(
          "Miktar",
          13
        )}` +
        `${padLeft(
          "Birim",
          7
        )}` +
        `${padLeft(
          "Br.Kur",
          13
        )}` +
        `${padLeft(
          "Has Fiy.",
          13
        )}` +
        `${padLeft(
          "Has Gr.",
          14
        )}\n`
    ),

    textBytes(
      horizontalLine()
    ),
  ];

  for (
    const row of
    list
  ) {
    const av =
      row
        ?.islemTur
        ?.endsWith(
          "ALINDI"
        )
        ? "A"
        : "V";

    /*
     * Gram:
     * Backend fiş modelinden gelen doğru değer.
     *
     * PC HTML tarafında da:
     * row.gram ?? row.toplam
     *
     * kullanılıyor.
     */
    const gram =
      row?.gram ??
      row?.toplam ??
      0;

    chunks.push(
      textBytes(
        `${padRight(
          av,
          4
        )}` +
          `${padLeft(
            fmtTR(
              row?.miktar,
              2
            ),
            13
          )}` +
          `${padLeft(
            row?.birim || "",
            7
          )}` +
          `${padLeft(
            fmtTR(
              row?.birimKur,
              2
            ),
            13
          )}` +
          `${padLeft(
            fmtTR(
              row?.guncelKur,
              2
            ),
            13
          )}` +
          `${padLeft(
            fmtTR(
              gram,
              2
            ),
            14
          )}\n`
      )
    );
  }

  chunks.push(
    textBytes(
      horizontalLine()
    )
  );

  return concatBytes(
    ...chunks
  );
};

/* ===========================================================
   BAĞLANTILI İŞLEMLER

   İlk Miktar = 18
   İlk Birim  = 14
   Son Miktar = 18
   Son Birim  = 14

   TOPLAM = 64
=========================================================== */

const buildBaglantiliBytes = (
  list
) => {
  if (
    !Array.isArray(
      list
    ) ||
    list.length === 0
  ) {
    return new Uint8Array();
  }

  const chunks = [
    sectionTitleBytes(
      "BAGLANTILI ISLEMLER"
    ),

    fontBBytes(),

    textBytes(
      `${padLeft(
        "Ilk Miktar",
        18
      )}` +
        `${padLeft(
          "Ilk Birim",
          14
        )}` +
        `${padLeft(
          "Son Miktar",
          18
        )}` +
        `${padLeft(
          "Son Birim",
          14
        )}\n`
    ),

    textBytes(
      horizontalLine()
    ),
  ];

  for (
    const row of
    list
  ) {
    chunks.push(
      textBytes(
        `${padLeft(
          fmtTR(
            row?.ilkMiktar,
            2
          ),
          18
        )}` +
          `${padLeft(
            row?.ilkBirim || "",
            14
          )}` +
          `${padLeft(
            fmtTR(
              row?.sonMiktar,
              2
            ),
            18
          )}` +
          `${padLeft(
            row?.sonBirim || "",
            14
          )}\n`
      )
    );
  }

  chunks.push(
    textBytes(
      horizontalLine()
    )
  );

  return concatBytes(
    ...chunks
  );
};

/* ===========================================================
   BAKİYE

   DOĞRU SIRA:

   Önceki | Sonraki | Fiş Top. | Birim

   Önceki  = 17
   Sonraki = 17
   Fiş Top = 20
   Birim   = 10

   TOPLAM = 64
=========================================================== */

const buildBakiyeBytes = (
  onceki = {},
  son = {}
) => {
  const chunks = [
    sectionTitleBytes(
      "BAKIYE"
    ),

    fontBBytes(),

    textBytes(
      `${padLeft(
        "Onceki",
        17
      )}` +
        `${padLeft(
          "Sonraki",
          17
        )}` +
        `${padLeft(
          "Fis Top.",
          20
        )}` +
        `${padLeft(
          "Birim",
          10
        )}\n`
    ),

    textBytes(
      horizontalLine()
    ),
  ];

  const addRow = (
    label,
    previousValue,
    nextValue
  ) => {
    const previous =
      Number(
        previousValue
      ) || 0;

    const next =
      Number(
        nextValue
      ) || 0;

    const total =
      next -
      previous;

    chunks.push(
      textBytes(
        `${padLeft(
          fmtTR(
            previous,
            2
          ),
          17
        )}` +
          `${padLeft(
            fmtTR(
              next,
              2
            ),
            17
          )}` +
          `${padLeft(
            fmtTR(
              total,
              2
            ),
            20
          )}` +
          `${padLeft(
            label,
            10
          )}\n`
      )
    );
  };

  addRow(
    "Gram",
    onceki?.gram,
    son?.gram
  );

  addRow(
    "TL",
    onceki?.tl,
    son?.tl
  );

  addRow(
    "USD",
    onceki?.usd,
    son?.usd
  );

  addRow(
    "EUR",
    onceki?.eur,
    son?.eur
  );

  chunks.push(
    textBytes(
      horizontalLine()
    )
  );

  return concatBytes(
    ...chunks
  );
};

/* ===========================================================
   ZİYNET BAKİYELERİ

   2 kolon:

   32 + 32 = 64
=========================================================== */

const buildZiynetBakiyeBytes = (
  ziynetBakiyeler
) => {
  if (
    !ziynetBakiyeler
  ) {
    return new Uint8Array();
  }

  const z =
    ziynetBakiyeler || {};

  const values = [
    [
      "Y.Ata",
      z.yeniAta,
    ],
    [
      "Y.Yrm",
      z.yeniYarim,
    ],
    [
      "Y.Cyr",
      z.yeniCeyrek,
    ],
    [
      "E.Ata",
      z.eskiAta,
    ],
    [
      "E.Yrm",
      z.eskiYarim,
    ],
    [
      "E.Cyr",
      z.eskiCeyrek,
    ],
  ];

  const chunks = [
    sectionTitleBytes(
      "ZIYNET BAKIYELERI"
    ),

    fontBBytes(),
  ];

  for (
    let i = 0;
    i < values.length;
    i += 2
  ) {
    const left =
      values[i];

    const right =
      values[i + 1];

    chunks.push(
      textBytes(
        `${padRight(
          `${left[0]}: ${fmtTR(
            left[1],
            0
          )}`,
          32
        )}` +
          `${padRight(
            `${right[0]}: ${fmtTR(
              right[1],
              0
            )}`,
            32
          )}\n`
      )
    );
  }

  chunks.push(
    textBytes(
      horizontalLine()
    )
  );

  return concatBytes(
    ...chunks
  );
};

/* ===========================================================
   GENEL ESC/POS FİŞ BUILDER
=========================================================== */

export const buildEscPosFis = ({
  fis = {},
  onceki = {},
  son = {},
  tarih = "",
  hesapAdi = "",
  firmaAdi = "",
  ziynetBakiyeler = null,
} = {}) => {
  const altinList =
    Array.isArray(
      fis?.altin
    )
      ? fis.altin
      : [];

  const ziynetList =
    Array.isArray(
      fis?.ziynet
    )
      ? fis.ziynet
      : [];

  const nakitList =
    Array.isArray(
      fis?.nakit
    )
      ? fis.nakit
      : [];

  const gramKarNakitList =
    Array.isArray(
      fis?.gramKarNakit
    )
      ? fis.gramKarNakit
      : [];

  const baglantiliList =
    Array.isArray(
      fis?.baglantili
    )
      ? fis.baglantili
      : [];

  return concatBytes(
    /* =======================================================
       INITIALIZE
    ======================================================= */

    bytes(
      ESC,
      0x40
    ),

    /* =======================================================
       TÜM MOBİL FİŞ FONT B

       ESC M 1
    ======================================================= */

    fontBBytes(),

    /* =======================================================
       LEFT MARGIN = 0
    ======================================================= */

    bytes(
      GS,
      0x4c,
      0x00,
      0x00
    ),

    /* =======================================================
       FİRMA BAŞLIK
    ======================================================= */

    bytes(
      ESC,
      0x61,
      0x01
    ),

    bytes(
      ESC,
      0x45,
      0x01
    ),

    textBytes(
      `${normalizePrinterText(
        firmaAdi ||
          "KUYUMCU"
      )}\n`
    ),

    bytes(
      ESC,
      0x45,
      0x00
    ),

    /* =======================================================
       TARİH
    ======================================================= */

    textBytes(
      `${normalizePrinterText(
        tarih || ""
      )}\n`
    ),

    /* =======================================================
       SOL HİZA
    ======================================================= */

    bytes(
      ESC,
      0x61,
      0x00
    ),

    /* =======================================================
       YATAY BAŞLANGIÇ = 0
    ======================================================= */

    bytes(
      ESC,
      0x24,
      0x00,
      0x00
    ),

    /* =======================================================
       HESAP
    ======================================================= */

    textBytes(
      `Hesap: ${truncate(
        hesapAdi || "",
        LINE_WIDTH - 7
      )}\n`
    ),

    textBytes(
      horizontalLine()
    ),

    /* =======================================================
       ALTIN
    ======================================================= */

    buildAltinTableBytes(
      altinList
    ),

    /* =======================================================
       ZİYNET
    ======================================================= */

    buildZiynetTableBytes(
      ziynetList
    ),

    /* =======================================================
       NAKİT
    ======================================================= */

    buildNakitTableBytes(
      nakitList
    ),

    /* =======================================================
       ALTIN KARŞILIĞI NAKİT
    ======================================================= */

    buildGramKarNakitBytes(
      gramKarNakitList
    ),

    /* =======================================================
       BAĞLANTILI
    ======================================================= */

    buildBaglantiliBytes(
      baglantiliList
    ),

    /* =======================================================
       BAKİYE
    ======================================================= */

    buildBakiyeBytes(
      onceki,
      son
    ),

    /* =======================================================
       ZİYNET BAKİYELERİ
    ======================================================= */

    ziynetList.length > 0
      ? buildZiynetBakiyeBytes(
          ziynetBakiyeler
        )
      : new Uint8Array(),

    /* =======================================================
       FOOTER
    ======================================================= */

    fontBBytes(),

    bytes(
      ESC,
      0x61,
      0x01
    ),

    textBytes(
      "MALI DEGERI YOKTUR\n"
    ),

    textBytes(
      "\n\n\n"
    ),

    /* =======================================================
       CUT
    ======================================================= */

    bytes(
      GS,
      0x56,
      0x00
    )
  );
};

/* ===========================================================
   BLE WRITE
=========================================================== */

const sleep = (
  ms
) =>
  new Promise(
    (
      resolve
    ) => {
      setTimeout(
        resolve,
        ms
      );
    }
  );

/* ===========================================================
   WRITE METHOD ÇAĞIR
=========================================================== */

const callWriteMethod = async (
  characteristic,
  methodName,
  chunk
) => {
  if (
    typeof characteristic?.[
      methodName
    ] !== "function"
  ) {
    return false;
  }

  await characteristic[
    methodName
  ](
    chunk
  );

  return true;
};

/* ===========================================================
   TEK CHUNK YAZ
=========================================================== */

const writeChunk = async (
  characteristic,
  chunk
) => {
  const methods = [];

  /* =======================================================
     WRITE WITHOUT RESPONSE
  ======================================================= */

  if (
    characteristic
      ?.properties
      ?.writeWithoutResponse
  ) {
    methods.push(
      "writeValueWithoutResponse"
    );
  }

  /* =======================================================
     WRITE WITH RESPONSE
  ======================================================= */

  if (
    characteristic
      ?.properties
      ?.write
  ) {
    methods.push(
      "writeValueWithResponse"
    );
  }

  /*
   * Eski Chromium / bazı iOS Web Bluetooth
   * implementasyonları.
   */
  methods.push(
    "writeValue"
  );

  let lastError =
    null;

  for (
    const methodName of
    [...new Set(methods)]
  ) {
    try {
      const wrote =
        await callWriteMethod(
          characteristic,
          methodName,
          chunk
        );

      if (wrote) {
        return;
      }
    } catch (
      error
    ) {
      lastError =
        error;

      console.warn(
        `Bluetooth ${methodName} başarısız, diğer yöntem deneniyor.`,
        error
      );
    }
  }

  throw (
    lastError ||
    new Error(
      "Bluetooth characteristic yazma desteklemiyor."
    )
  );
};

/* ===========================================================
   CHUNK RETRY
=========================================================== */

const writeChunkWithRetry = async (
  characteristic,
  chunk,
  maxAttempts = 3
) => {
  let lastError =
    null;

  for (
    let attempt = 1;
    attempt <= maxAttempts;
    attempt++
  ) {
    try {
      await writeChunk(
        characteristic,
        chunk
      );

      return;
    } catch (
      error
    ) {
      lastError =
        error;

      if (
        attempt <
        maxAttempts
      ) {
        await sleep(
          80 *
            attempt
        );
      }
    }
  }

  throw lastError;
};

/* ===========================================================
   HAM ESC/POS GÖNDER
=========================================================== */

export const sendEscPosBytes = async (
  data
) => {
  if (
    !(
      data instanceof
      Uint8Array
    )
  ) {
    throw new Error(
      "Yazıcı verisi Uint8Array olmalıdır."
    );
  }

  if (
    data.length === 0
  ) {
    throw new Error(
      "Yazıcıya gönderilecek veri boş."
    );
  }

  /* =======================================================
     BAĞLANTI
  ======================================================= */

  if (
    !isBluetoothPrinterConnected()
  ) {
    await connectBluetoothPrinter();
  }

  if (
    !writeCharacteristic
  ) {
    throw new Error(
      "Bluetooth yazma kanalı hazır değil."
    );
  }

  /* =======================================================
     BLE CHUNK

     20 byte:
     düşük MTU'lu BLE cihazlarda güvenli değerdir.
  ======================================================= */

  const CHUNK_SIZE =
    20;

  const CHUNK_DELAY_MS =
    25;

  console.log(
    "ESC/POS gönderim başlıyor:",
    {
      totalBytes:
        data.length,

      chunkSize:
        CHUNK_SIZE,
    }
  );

  try {
    for (
      let offset = 0;
      offset <
      data.length;
      offset +=
        CHUNK_SIZE
    ) {
      const chunk =
        data.slice(
          offset,
          offset +
            CHUNK_SIZE
        );

      await writeChunkWithRetry(
        writeCharacteristic,
        chunk,
        3
      );

      if (
        offset +
          CHUNK_SIZE <
        data.length
      ) {
        await sleep(
          CHUNK_DELAY_MS
        );
      }
    }
  } catch (
    error
  ) {
    console.error(
      "ESC/POS Bluetooth gönderim hatası:",
      error
    );

    /*
     * Bir sonraki baskıda temiz bağlantı kurulsun.
     */
    try {
      bluetoothDevice
        ?.gatt
        ?.disconnect();
    } catch {
      // ignore
    }

    resetConnection();

    throw error;
  }

  console.log(
    "ESC/POS gönderim tamamlandı:",
    {
      totalBytes:
        data.length,
    }
  );
};

/* ===========================================================
   VARSAYILAN BLUETOOTH FİŞ BASKISI

   Mobil:

   params
      ↓
   buildEscPosFis
      ↓
   Font B / 64 karakter ESC/POS
      ↓
   BLE
      ↓
   yazıcı
=========================================================== */

export const printBluetoothFis = async (
  params = {}
) => {
  const data =
    buildEscPosFis(
      params
    );

  await sendEscPosBytes(
    data
  );
};

/* ===========================================================
   LEGACY HTML -> RASTER BLUETOOTH

   GERİYE DÖNÜK UYUMLULUK İÇİN KORUNUYOR.

   Yeni HesapIslemFisPrintService bunu kullanmıyor.
=========================================================== */

export const printBluetoothHtml = async (
  html,
  options = {}
) => {
  if (
    typeof html !== "string" ||
    !html.trim()
  ) {
    throw new Error(
      "Bluetooth raster baskı için geçerli bir HTML verilmedi."
    );
  }

  /* =======================================================
     BAĞLANTI
  ======================================================= */

  if (
    !isBluetoothPrinterConnected()
  ) {
    await connectBluetoothPrinter();
  }

  /* =======================================================
     HTML -> RASTER
  ======================================================= */

  const escPosData =
    await htmlToEscPosRaster(
      html,
      {
        printerWidth:
          options
            .printerWidth ??
          576,

        scale:
          options
            .scale ??
          2,

        threshold:
          options
            .threshold ??
          180,

        bottomPadding:
          options
            .bottomPadding ??
          24,

        initialize:
          options
            .initialize ??
          true,

        zeroMargin:
          options
            .zeroMargin ??
          true,

        feed:
          options
            .feed ??
          true,

        feedLines:
          options
            .feedLines ??
          3,

        cut:
          options
            .cut ??
          true,
      }
    );

  /* =======================================================
     BLE
  ======================================================= */

  await sendEscPosBytes(
    escPosData
  );
};

/* ===========================================================
   STANDART HTML / BROWSER PRINT

   PC:
   HTML
      ↓
   yeni pencere
      ↓
   window.print()

   Mobil Bluetooth başarısız olursa fallback olarak da
   kullanılabilir.
=========================================================== */

export const printHtmlFallback = (
  html
) => {
  if (
    typeof window ===
    "undefined"
  ) {
    throw new Error(
      "Tarayıcı yazdırma özelliği kullanılamıyor."
    );
  }

  if (
    typeof html !== "string" ||
    !html.trim()
  ) {
    throw new Error(
      "Yazdırılacak HTML içeriği bulunamadı."
    );
  }

  /* =======================================================
     PRINT WINDOW
  ======================================================= */

  const win =
    window.open(
      "",
      "_blank"
    );

  if (!win) {
    throw new Error(
      "Yazdırma penceresi açılamadı. Tarayıcı popup engelleyicisini kontrol edin."
    );
  }

  /* =======================================================
     HTML
  ======================================================= */

  win.document.open();

  win.document.write(
    html
  );

  win.document.close();

  /* =======================================================
     PENCERE KAPAT
  ======================================================= */

  let closed =
    false;

  const closePrintWindow =
    () => {
      if (closed) {
        return;
      }

      closed =
        true;

      try {
        win.close();
      } catch {
        // ignore
      }
    };

  /* =======================================================
     PRINT
  ======================================================= */

  const runPrint =
    async () => {
      try {
        /* =================================================
           FONTLARI BEKLE
        ================================================= */

        if (
          win.document
            ?.fonts
            ?.ready
        ) {
          try {
            await win.document
              .fonts
              .ready;
          } catch {
            /*
             * Font hatası baskıyı durdurmasın.
             */
          }
        }

        win.focus();

        win.print();
      } catch (
        printError
      ) {
        console.error(
          "Browser print hatası:",
          printError
        );

        throw printError;
      }
    };

  /* =======================================================
     AFTER PRINT
  ======================================================= */

  try {
    win.addEventListener(
      "afterprint",
      closePrintWindow,
      {
        once: true,
      }
    );
  } catch {
    /*
     * Eski browser.
     */
  }

  /* =======================================================
     DOCUMENT READY
  ======================================================= */

  if (
    win.document
      .readyState ===
    "complete"
  ) {
    setTimeout(
      () => {
        runPrint();
      },
      50
    );
  } else {
    win.onload =
      () => {
        setTimeout(
          () => {
            runPrint();
          },
          50
        );
      };
  }
};